import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { DEFAULT_LIGHTING, type Box, type LevelGeometry, type LightingSettings } from '../core/level.ts';
import { STAND_EYE } from '../core/constants.ts';
import type { Vec3 } from '../core/math.ts';
import type { Barrier } from '../core/placed.ts';
import type { BodyMove, RayHit, WorldQuery } from '../core/sim.ts';
import { CharacterBodies } from '../net/characterBodies.ts';
import { LevelColliders, RAY_GROUPS } from '../net/levelColliders.ts';
import type { PlayerState } from '../core/player.ts';
import { AssetRegistry } from './assets/registry.ts';
import type { AssetRole } from './assets/roles.ts';

/**
 * How many metres of wall one tile of texture covers.
 *
 * Baked into each box's UVs rather than set on the material, because the
 * material is shared by every surface of its kind and `repeat` is a property of
 * the texture — one setting for the whole arena. A wall forty metres long and a
 * wall two metres long would then carry the same number of bricks, one of them
 * the size of a door. Per-box UVs give a brick a fixed size in the world, which
 * is the only way a room reads as built rather than as printed.
 */
const TEXTURE_METRES = 2.4;

/** Shared by kind, since a material per box is a draw call per box. */
function surface(name: string, opts: THREE.MeshStandardMaterialParameters = {}) {
  const load = (file: string, srgb = false): THREE.Texture => {
    const t = new THREE.TextureLoader().load(`/textures/${name}_${file}.jpg`);
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
    // Base colour is authored in sRGB; normals and roughness are data and must
    // not be colour-managed, or the lighting comes out subtly wrong everywhere.
    if (srgb) t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 4;
    return t;
  };
  return new THREE.MeshStandardMaterial({
    map: load('basecolor', true),
    normalMap: load('normal'),
    roughnessMap: load(opts.roughness === undefined ? 'rough' : 'rough'),
    roughness: 1,
    metalness: 0.02,
    ...opts,
  });
}

/*
 * Built on first use, not at import.
 *
 * `TextureLoader` reaches for `Image`, which does not exist in Node — and this
 * module is imported by tests for the arithmetic in it (`tileUvs`,
 * `visibilityWeight`) that has nothing to do with a browser. Loading at module
 * scope made importing the file at all a `ReferenceError`.
 */
let MATERIAL_CACHE: Record<string, THREE.MeshStandardMaterial> | null = null;

function materials(): Record<string, THREE.MeshStandardMaterial> {
  if (MATERIAL_CACHE) return MATERIAL_CACHE;
  MATERIAL_CACHE = {
    floor: surface('unevenbrick', { color: 0xb9b2a4 }),
    wall: surface('brick', { color: 0xb5aea1 }),
    ceiling: new THREE.MeshStandardMaterial({ color: 0x2a2723, roughness: 1 }),
  // Altars and columns are dressed stone rather than masonry.
    platform: surface('plaster', { color: 0xc2b9a6 }),
    pillar: surface('plaster', { color: 0xbdb4a2 }),
  // Crates, shelves and the like. Warmer than the stone so scenery reads as
  // *placed* rather than as part of the building, even before it has a model.
    prop: new THREE.MeshStandardMaterial({ color: 0x6b5940, roughness: 0.92 }),
  };
  return MATERIAL_CACHE;
}

/**
 * Box kinds a model may stand in for, and the role that supplies it.
 *
 * The box is the thing; the model is the clothes. Collision, line of sight and
 * spell placement all read the box, so dressing one of these later moves nothing
 * — and a role the manifest has no model for simply keeps its slab, which is why
 * a level is playable before any of it is modelled.
 *
 * Floors, walls and ceilings are deliberately absent. They are generated at
 * arbitrary extents and a model would have to be stretched to fit; replacing
 * those wants a modular kit, which is a decision rather than a scale factor.
 */
const MODELLED: Record<string, AssetRole> = {
  pillar: 'level.pillar',
  prop: 'level.crate',
};

/**
 * Props whose node name says which model they want, most specific first.
 *
 * The blockout carries its Godot node name through as `room`, which is the only
 * thing distinguishing a shelf from a crate — both are `prop` boxes, because
 * both are scenery that blocks, and core has no reason to care which. Anything
 * unmatched falls through to the kind's default, so a new crate variant works
 * the day it is placed and a new *kind* of thing needs one line here.
 */
/*
 * Which model dresses a prop, by the name it carries out of the level editor.
 *
 * Ordered, and the specific patterns come first: `MetalCrate` and
 * `FarmCrate_Carrot` both contain "crate", so a general crate rule placed above
 * them would swallow both and every metal crate in the cathedral would be made
 * of wood.
 */
const BY_NAME: [RegExp, AssetRole][] = [
  [/metalcrate|crate_metal/i, 'prop.crateMetal'],
  [/farmcrate|carrot/i, 'prop.crateFarm'],
  [/workbench/i, 'prop.workbench'],
  [/bench/i, 'prop.bench'],
  [/cauldron/i, 'prop.cauldron'],
  [/cage/i, 'prop.cage'],
  [/arch/i, 'prop.arch'],
  [/roof_?support/i, 'prop.roofSupport'],
  [/chain/i, 'prop.chain'],
  [/shelf|book/i, 'level.bookshelf'],
  [/crate|box/i, 'level.crate'],
];

/**
 * Props the level places at something other than their authored size.
 *
 * Short, and getting shorter. Anything *instanced* in the level editor carries
 * its own scale in its transform, and the generator now bakes that into the box
 * — so the Cage's doubling arrives in the geometry and repeating it here would
 * make it four times the size.
 *
 * What is left is the blockout boxes, which have a size but no scale: the metal
 * crate replaced wooden ones that were larger than it is, and its box says so
 * while its model does not.
 */
const PROP_SCALE: Partial<Record<AssetRole, number>> = {
  'prop.crateMetal': 1.5,
};

/**
 * How much of a model's own size to leave as a gap when repeating it.
 *
 * Shelves butted exactly edge to edge z-fight along the seam and read as one
 * extruded object; a few centimetres of daylight makes them count as separate
 * pieces of furniture.
 */
const TILE_GAP = 0.04;

/**
 * Which model, if any, should stand in for this box.
 *
 * Two conditions, and both matter. The kind has to be one that *can* be a
 * discrete object, and the manifest has to actually have the model — a missing
 * model is not an error, it is a box that has not been dressed yet, and the slab
 * it falls back to is a perfectly serviceable crate in the meantime.
 *
 * A finer name-based mapping (bookshelves distinct from crates) can key off
 * `b.room`, which carries the node name straight from the blockout. Kind first,
 * because a name is a label and a kind is a promise.
 */
function roleFor(b: Box, assets: AssetRegistry): AssetRole | null {
  const named = BY_NAME.find(([pattern]) => pattern.test(b.room ?? ''))?.[1];
  const role = named ?? MODELLED[b.kind];
  return role && assets.usesModel(role) ? role : null;
}

/**
 * How many torch fixtures can be lit at once.
 *
 * Every lit material loops over every light in the scene, so this is paid by the
 * whole arena on every pixel. Fourteen is enough that a room reads as lit by its
 * own braziers; the hundred-odd fixtures beyond it are mesh and nothing more.
 */
const TORCH_LIGHT_BUDGET = 14;
/** Frames between re-deciding which fixtures hold the lights. */
const TORCH_RECHECK_FRAMES = 10;

/**
 * Half-width of the shadow camera, metres. It follows the player.
 *
 * The first version covered the whole arena — 110 metres square — so every one
 * of the Cathedral's 307 boxes and 65 fixtures fell inside the frustum and was
 * redrawn into the depth map sixty times a second, at five and a half
 * centimetres a texel. Soft to the point of mush, which is part of why it needed
 * `PCFSoft` to read as anything.
 *
 * Sixty-four metres across is the compromise, and it is worth being honest that
 * it *is* one. The Cathedral floor is 77 by 72, so anything much above this
 * covers the whole arena wherever the player stands and culls nothing at all —
 * ±40 would have been a texel-density change wearing a performance change's
 * clothes. Below it, the saving is real: a third of the ground, so a third of
 * the geometry reaching the depth pass, and three centimetres a texel.
 *
 * What it buys is bounded by what it costs, which is that the arena's fog is
 * lighter than it looks — `fogDensity` 0.013 leaves a wall at thirty metres only
 * sixteen per cent hazed — so a static shadow *can* be seen to wink out at the
 * boundary if somebody is looking for it. Mages are not affected: they stop
 * casting at sixteen metres anyway. It is the pillars that pop, and how much
 * that matters is a question for eyes rather than for arithmetic, which is why
 * this is reachable from `mw.perf.shadowExtent`.
 */
const SHADOW_EXTENT = 32;
/** How far up the light sits. Far enough to clear the tallest vault. */
const SHADOW_DISTANCE = 110;
/** How fast a slot brightens or dims when it changes hands, per second. */
const TORCH_FADE_SPEED = 3;
/**
 * How many nearby fixtures are tested for visibility, as a multiple of the pool.
 *
 * Every ray costs something and a fixture forty metres away is not going to win
 * a slot whether it is visible or not, so the search is bounded. Three times the
 * pool is enough that a whole room's worth of near torches being hidden still
 * finds the visible ones behind them.
 */
const TORCH_CANDIDATE_FACTOR = 3;
/**
 * What an entirely hidden fixture's distance is multiplied by.
 *
 * Not infinity. A torch around a corner still lights the corner you can see, so
 * it stays in the running and simply loses to anything visible — at eight times
 * the effective distance, which in practice means it is picked only when the
 * room has nothing else to offer.
 */
const TORCH_OCCLUDED_WEIGHT = 1 / 8;
/**
 * Where a fixture is sampled, relative to the flame.
 *
 * The centre plus four offsets roughly a bracket wide. One ray to the middle is
 * the single worst choice available: the centre is exactly the point an edge
 * clips first, so a torch seen past a pillar strobes as you walk. Spreading the
 * samples turns that into a value that slides.
 */
const TORCH_SAMPLES: readonly (readonly [number, number, number])[] = [
  [0, 0, 0],
  [0.35, 0, 0], [-0.35, 0, 0],
  [0, 0, 0.35], [0, 0, -0.35],
];
/** The cloister sky: dimmer than a torch, far wider, and it never gutters. */
const MOONLIGHT_INTENSITY = 28;
/**
 * How many skylights a map may have lit at once.
 *
 * Fixed for the same reason the torch budget is: the light count is part of
 * every lit material's shader key, so a pool that resized between maps would
 * recompile the arena mid-rotation. Two covers every map drawn so far.
 */
const MOONLIGHT_BUDGET = 2;
const MOONLIGHT_RANGE = 42;

/**
 * What a fixture's distance is divided by, given how many of its samples the eye
 * could reach.
 *
 * Exported because it is the whole of the policy and it is arithmetic: a test
 * for it needs no scene, no physics and no lights. The two properties that
 * matter are that nothing is ever excluded outright — a torch round a corner
 * still lights the corner you can see — and that partial visibility is a real
 * answer rather than a rounding of yes and no.
 */
export function visibilityWeight(seen: number, samples: number): number {
  if (samples <= 0) return 1;
  const share = Math.max(0, Math.min(samples, seen)) / samples;
  return TORCH_OCCLUDED_WEIGHT + (1 - TORCH_OCCLUDED_WEIGHT) * share;
}

/**
 * Rewrites a box's UVs so its texture tiles at a fixed size in the world.
 *
 * `BoxGeometry` gives every face the unit square, so a texture stretches to
 * whatever that face happens to be — bricks the size of a door on a long wall
 * and postage stamps on a short one. Each face is scaled here by its own two
 * dimensions instead, in the order Three lays them out: +X, −X, +Y, −Y, +Z, −Z,
 * four vertices each.
 *
 * The consequence worth knowing is that adjacent boxes do not align their
 * courses — two walls meeting at a corner each start their brickwork at their
 * own origin. At this tile size and in this light it does not read; a modular
 * kit is the fix if it ever does.
 */
export function tileUvs(geometry: THREE.BufferGeometry, size: Vec3): void {
  const uv = geometry.getAttribute('uv');
  const spans: [number, number][] = [
    [size.z, size.y], [size.z, size.y],
    [size.x, size.z], [size.x, size.z],
    [size.x, size.y], [size.x, size.y],
  ];

  for (let face = 0; face < spans.length; face++) {
    const [w, h] = spans[face]!;
    for (let i = 0; i < 4; i++) {
      const v = face * 4 + i;
      uv.setXY(v, uv.getX(v) * (w / TEXTURE_METRES), uv.getY(v) * (h / TEXTURE_METRES));
    }
  }
  uv.needsUpdate = true;
}


/**
 * Fills a box with as many copies of its model as honestly fit.
 *
 * A run of library shelving is one long box in the blockout, and one model
 * stretched to fill it is a bookcase three metres wide — which reads as a
 * mistake, because it is one. Repeating instead keeps the furniture furniture
 * sized and lets the level designer draw a *run* rather than count pieces.
 *
 * Along the box's longer horizontal axis only. Turning this into a full 2D tiler
 * would be a floor of crates from one wall to the other, and a storage room is
 * meant to be walked through.
 *
 * The box is never consulted for anything but extent: collision was already
 * created from it, so however many pieces come out of here, and whatever they
 * are, a mage collides with exactly the volume the .tscn drew.
 */
/**
 * How many copies of a `width`-wide model fill a `run`-long box.
 *
 * Exported because it is the part of dressing that can be wrong arithmetically
 * rather than visually, and it is worth a test that needs no scene. Never zero:
 * a box too small for its model still gets one, because scenery that silently
 * vanishes is worse than scenery that overhangs.
 */
export function tileCount(run: number, width: number): number {
  if (!(width > 0.05)) return 1;
  return Math.max(1, Math.round(run / (width + TILE_GAP)));
}

function dressBox(b: Box, role: AssetRole, assets: AssetRegistry): THREE.Object3D[] {
  const alongX = b.size.x >= b.size.z;
  const run = alongX ? b.size.x : b.size.z;

  const first = assets.create({ role, size: b.size });
  /*
   * Scaled before it is measured.
   *
   * A prop the level wants at twice its authored size is twice as wide, and the
   * tiling arithmetic below divides a run by that width — measuring first would
   * lay out a row of double-sized cages spaced for single-sized ones, and they
   * would overlap.
   */
  const scale = PROP_SCALE[role] ?? 1;
  if (scale !== 1) first.scale.setScalar(scale);

  const unit = new THREE.Box3().setFromObject(first).getSize(new THREE.Vector3());
  const width = alongX ? unit.x : unit.z;

  const count = tileCount(run, width);
  const step = run / count;
  const start = -run / 2 + step / 2;

  const pieces: THREE.Object3D[] = [];
  const yaw = b.yaw ?? 0;
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  for (let i = 0; i < count; i++) {
    const piece = i === 0 ? first : assets.create({ role, size: b.size });
    if (i > 0 && scale !== 1) piece.scale.setScalar(scale);
    const offset = start + i * step;
    // Laid out along the box's own axis, then turned with it — so a shelf run in
    // an angled room stays a run rather than fanning across the floor.
    const lx = alongX ? offset : 0;
    const lz = alongX ? 0 : offset;
    piece.position.set(
      b.pos.x + lx * cos + lz * sin,
      b.pos.y - b.size.y / 2,
      b.pos.z - lx * sin + lz * cos,
    );
    // Shelves face out of the run rather than along it, so a row presents its
    // spines to whoever is walking past.
    piece.rotation.y = yaw + (alongX ? 0 : Math.PI / 2);
    piece.traverse((n) => { n.castShadow = true; n.receiveShadow = true; });
    pieces.push(piece);
  }
  return pieces;
}

// Defined once, in `net/levelColliders.ts`, and shared with the server's world.
export {
  CHARACTER_GROUPS, GROUP_CHARACTER, GROUP_LEVEL, LEVEL_GROUPS, MOVE_FILTER,
} from '../net/levelColliders.ts';

/**
 * Owns the Rapier world and the level meshes, both built from the same
 * LevelGeometry so collision can never disagree with what you can see.
 * Also answers core's one question about the world: is this line blocked.
 */
export class GameWorld implements WorldQuery {
  readonly scene = new THREE.Scene();
  readonly physics: RAPIER.World;
  /** Exposed for live tuning from the console. */
  readonly torches: THREE.PointLight[] = [];
  /** Where the fixtures are. Far more of these than there are lights. */
  private torchSpots: Vec3[] = [];
  private torchRecheck = 0;
  /** Which fixture each slot currently holds, or −1 for none. */
  private readonly torchHeld: number[] = [];
  /** Where each slot's brightness is heading, and where it has got to. */
  private readonly torchTarget: number[] = [];
  private readonly torchFade: number[] = [];
  ambient!: THREE.AmbientLight;
  moon!: THREE.DirectionalLight;
  readonly lighting: Required<LightingSettings>;
  /** Unit vector from the moon's target towards the moon. Fixed. */
  private readonly shadowDir = new THREE.Vector3();
  /** The shadow camera's own right and up, so its centre can be texel-snapped. */
  private readonly shadowRight = new THREE.Vector3();
  private readonly shadowUp = new THREE.Vector3();
  private readonly shadowCentre = new THREE.Vector3();
  /** Per-torch phase, so eight flames do not gutter in unison. */
  private readonly torchPhase: number[] = [];
  private readonly torchBase: number[] = [];
  /** Everything the level put in the scene, so a map change can take it back. */
  private levelGroup: THREE.Group | null = null;
  /**
   * The level, the sky lids and the solid spell walls — built by the builder the
   * server's world uses too, so the two cannot build different levels. The
   * player's capsule is the controller's, and is not here.
   */
  readonly colliders: LevelColliders;
  /** The registry the level was dressed from, kept for rebuilding. */
  private readonly assets: AssetRegistry;
  /**
   * Moonlights, pooled at a fixed size for the same reason the torches are.
   *
   * The *number* of lights in a scene is part of every lit material's shader
   * program key, so a map with two skylights following one with none would
   * recompile the entire arena mid-round — which is the stall a map change
   * exists to avoid. A fixed pool, parked dark when a map wants fewer, keeps the
   * count constant across every map in a rotation.
   */
  private readonly moonlights: THREE.PointLight[] = [];

  private readonly ray = new RAPIER.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 1 });

  /**
   * The parts of the quality tier that cannot be changed afterwards.
   *
   * The light count is in every lit material's shader — it is part of the
   * program cache key — so changing it means recompiling every material in the
   * arena, which is precisely the stall the loading screen exists to avoid. The
   * map size allocates a texture. Both are therefore constructor arguments and
   * a tier change costs a reload, which is honest and is what the setting says.
   *
   * Everything else about the shadows can be turned live, and is: see
   * `setShadowExtent` and `renderer.shadowMap.autoUpdate`.
   */
  constructor(
    geometry: LevelGeometry,
    assets: AssetRegistry = AssetRegistry.grayBox(),
    quality: { torchLights?: number; shadowMapSize?: number; shadowExtent?: number } = {},
  ) {
    this.assets = assets;
    const torchLights = quality.torchLights ?? TORCH_LIGHT_BUDGET;
    const shadowMapSize = quality.shadowMapSize ?? 2048;
    const shadowExtent = quality.shadowExtent ?? SHADOW_EXTENT;
    this.physics = new RAPIER.World({ x: 0, y: -9.81, z: 0 });
    this.colliders = new LevelColliders(this.physics);
    this.bodies = new CharacterBodies(this.physics);

    const lit = { ...DEFAULT_LIGHTING, ...(geometry.lighting ?? {}) };
    this.lighting = lit;

    this.scene.background = new THREE.Color(lit.skyColor);
    this.scene.fog = new THREE.FogExp2(lit.fogColor, lit.fogDensity);

    // Three.js uses physical light units from r155 onward: point light intensity
    // is candela and falls off as 1/d². The first pass used values tuned for the
    // old unitless model, which put a torch at roughly a tenth of a lux ten
    // metres out — hence walls that rendered black while emissive shrines and
    // pools stayed visible.
    this.ambient = new THREE.AmbientLight(lit.moonColor, geometry.ambient);
    this.scene.add(this.ambient);

    // Cold from above and warm from below. The hemisphere light is doing the job
    // a bounce pass would: without it every downward-facing surface goes to pure
    // black, and a room where the floor reads and the undersides do not looks
    // broken rather than dark.
    const bounce = new THREE.HemisphereLight(lit.moonColor, lit.bounceColor, lit.bounceIntensity);
    this.scene.add(bounce);

    // A cold slot of moonlight from above, so silhouettes read in the dark.
    this.moon = new THREE.DirectionalLight(lit.moonColor, lit.moonIntensity);
    this.moon.position.set(-40, 90, -30);
    // The direction is what matters and it never changes; the *position* is now
    // a thing that moves with the player, so it is recorded before anything
    // touches it.
    this.shadowDir.copy(this.moon.position).normalize();
    if (lit.shadows) {
      // One shadow-casting light is the whole budget. Eight torches each want a
      // cube map, which is six renders apiece; the moon is one orthographic pass
      // and it is the light that actually defines the silhouettes.
      this.moon.castShadow = true;
      this.moon.shadow.mapSize.set(shadowMapSize, shadowMapSize);
      const cam = this.moon.shadow.camera;
      cam.left = -shadowExtent;
      cam.right = shadowExtent;
      cam.top = shadowExtent;
      cam.bottom = -shadowExtent;
      cam.near = 1;
      // Reaches from the light down past the arena floor, and past Valhalla's
      // ceiling — nothing below that casts anything anybody sees.
      cam.far = SHADOW_DISTANCE + 80;
      // Acne on a flat floor lit at a glancing angle is the usual failure here.
      this.moon.shadow.bias = -0.0008;
      this.moon.shadow.normalBias = 0.03;
    }
    this.scene.add(this.moon);
    /*
     * The target has to be in the scene now that it moves.
     *
     * A `DirectionalLight` aims at `target.matrixWorld`, and Three.js only
     * updates that for objects in the graph. Left out — which is the usual way,
     * because the default target sits at the origin and a static light aims at
     * the origin quite happily — the light would keep aiming at wherever the
     * matrix was last computed, which is to say at nothing.
     */
    this.scene.add(this.moon.target);
    this.buildShadowBasis();

    this.buildLevel(geometry);

    /*
     * A hundred and one torches, and fourteen lights between them.
     *
     * One `PointLight` per fixture was fine at a dozen and is not a thing that
     * can be done at a hundred: every lit material in the scene carries a loop
     * over every light, and past thirty or so the shader stops compiling at all
     * on some drivers. The count is also part of the program cache key, so a
     * hundred is both slow and brittle.
     *
     * So the fixtures are data and the lights are a pool. The nearest few
     * torches are lit and the rest are dark, which is invisible in play — a
     * torch forty metres away across a lit cathedral contributes nothing you
     * could point at — and the pool never changes size, which is the property
     * that the loading screen depends on.
     */
    /*
     * The pool is the budget, not the map's torch count.
     *
     * It used to be `min(budget, fixtures)`, which is the same thing on a map
     * with plenty of torches and a different number on one with few — and a
     * different number of lights is a different shader program for every lit
     * material in the arena. Rotating from a cathedral with sixty-five fixtures
     * to a bazaar with twelve would have recompiled the world in the middle of a
     * round. Extra slots sit dark and cost a few uniforms.
     */
    for (let i = 0; i < torchLights; i++) {
      const light = new THREE.PointLight(lit.torchColor, 0, geometry.torchRange, 2);
      this.scene.add(light);
      this.torches.push(light);
      this.torchBase.push(geometry.torchIntensity);
      this.torchHeld.push(-1);
      this.torchTarget.push(0);
      this.torchFade.push(0);
      // Unrelated phases, so the room never gutters in unison.
      this.torchPhase.push((i + 1) * 2.399);
    }

    /*
     * Moonlight: cool, steady, and outside the torch budget.
     *
     * There are one or two of these and they mark a space open to the sky, so
     * they get lights of their own rather than competing with the torches for a
     * slot — a cloister going dark because somebody walked into a corridor full
     * of braziers would be exactly wrong.
     */
    for (let i = 0; i < MOONLIGHT_BUDGET; i++) {
      const light = new THREE.PointLight(lit.moonColor, 0, MOONLIGHT_RANGE, 2);
      this.scene.add(light);
      this.moonlights.push(light);
    }

  }

  /** True when level geometry blocks the segment. */
  /**
   * Every mage's capsule, moved and placed by the same class the server uses.
   *
   * In a local match the room syncs every mage into it each tick and core moves
   * them all through it — bots included, so they bump into walls and into you
   * by exactly the rules a hosted server applies. In a hosted match the room is
   * on the server: the frame loop syncs the latest snapshot into it, and only
   * the local player is moved here, by prediction.
   */
  readonly bodies: CharacterBodies;

  moveBody(p: PlayerState, delta: Vec3, dt: number): BodyMove | null {
    return this.bodies.moveBody(p, delta, dt);
  }

  /** Every mage in the roster gets a capsule; anybody not in it loses theirs. */
  syncBodies(players: readonly PlayerState[]): void {
    this.bodies.sync(players);
  }

  /** Puts a body exactly where the simulation says, after a teleport. */
  place(p: PlayerState): void {
    this.bodies.place(p);
  }

  segmentBlocked(from: Vec3, to: Vec3): boolean {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-6) return false;

    this.ray.origin = from;
    this.ray.dir = { x: dx / len, y: dy / len, z: dz / len };
    const hit = this.physics.castRay(this.ray, len, true, undefined, RAY_GROUPS);
    return hit !== null;
  }

  /**
   * First level geometry along the ray. Same collision group filter as
   * `segmentBlocked`, so a ground-targeted spell cannot land on a player's head.
   *
   * `timeOfImpact` is a distance only because `dir` is unit length; the
   * normalisation below is what makes that true regardless of what core passes.
   */
  raycast(from: Vec3, dir: Vec3, maxDistance: number): RayHit | null {
    const len = Math.hypot(dir.x, dir.y, dir.z);
    if (len < 1e-6) return null;
    const unit = { x: dir.x / len, y: dir.y / len, z: dir.z / len };

    this.ray.origin = from;
    this.ray.dir = unit;
    const hit = this.physics.castRay(this.ray, maxDistance, true, undefined, RAY_GROUPS);
    if (!hit) return null;

    const d = hit.timeOfImpact;
    return {
      point: { x: from.x + unit.x * d, y: from.y + unit.y * d, z: from.z + unit.z * d },
      distance: d,
    };
  }

  /**
   * Mirrors the simulation's solid barriers into Rapier. See
   * `LevelColliders.syncBarriers`; the server's world does exactly the same.
   */
  syncBarriers(barriers: readonly Barrier[]): void {
    this.colliders.syncBarriers(barriers);
  }



  /**
   * Builds a map's geometry, lights and flames into the scene.
   *
   * Separate from the constructor because a set rotates maps, and a rotation
   * must not rebuild the *world* — the scene object, the renderer's programs,
   * the post-processing chain and the actors all hold references to it, and
   * replacing it would mean rebuilding every one of them and re-warming every
   * shader. What changes between maps is the contents; what stays is everything
   * pointing at them.
   */
  private buildLevel(geometry: LevelGeometry): void {
    const lit = { ...DEFAULT_LIGHTING, ...(geometry.lighting ?? {}) };
    const assets = this.assets;

    const group = new THREE.Group();
    for (const b of geometry.boxes) {
      // Pillars are discrete objects, so they can carry a model. Floors, walls
      // and ceilings are generated at arbitrary extents and would have to be
      // stretched to fit; replacing those wants a modular kit, which is a
      // decision rather than a scale factor.
      const role = roleFor(b, assets);
      if (role) {
        for (const piece of dressBox(b, role, assets)) group.add(piece);
      } else {
        const boxGeometry = new THREE.BoxGeometry(b.size.x, b.size.y, b.size.z);
        tileUvs(boxGeometry, b.size);
        const mesh = new THREE.Mesh(boxGeometry, materials()[b.kind] ?? materials().wall);
        mesh.position.set(b.pos.x, b.pos.y, b.pos.z);
        mesh.rotation.y = b.yaw ?? 0;
        // Floors and ceilings only receive. A floor casting onto itself buys
        // nothing but shadow acne, and doubles what the map has to draw.
        mesh.receiveShadow = true;
        mesh.castShadow = b.kind !== 'floor' && b.kind !== 'ceiling';
        group.add(mesh);
      }

    }

    /*
     * Collision comes from the boxes, never from the models, and from the same
     * builder the server uses — ceilings, the invisible lids over any skylight,
     * and later the solid spell walls. An art pass must not be able to move a
     * wall, and neither end may build a level the other does not have.
     */
    this.colliders.build(geometry);

    // Every fixture still gets its flame: a mesh is cheap, frustum-culled, and
    // a torch bracket with no fire in it looks broken from across the room.
    for (const t of geometry.torches) {
      // Flames do not cast: they are the light source, and a lamp casting its
      // own shadow reads as a hole in the light.
      const flame = assets.create({ role: 'prop.torch' });
      // A half turn: the fixture's yaw points away from the wall, and the model
      // reaches along its own +Z, which is the opposite of the game's forward.
      flame.rotation.y = t.yaw + Math.PI;
      /*
       * Hung so its *top* is at the marker, not its origin.
       *
       * A marker is where the level designer put the flame, and the light goes
       * there — but the bracket is modelled around its own middle, so placing it
       * at the marker leaves the fire coming out of the bracket's midriff with
       * the light glowing below it. The offset is measured from the model rather
       * than written down, so a different torch hangs correctly without anyone
       * remembering to change a number.
       */
      const bounds = new THREE.Box3().setFromObject(flame);
      flame.position.set(t.pos.x, t.pos.y - bounds.max.y, t.pos.z);
      group.add(flame);
    }

    this.scene.add(group);
    this.levelGroup = group;

    // Where the pooled lights may go. The pool itself does not change size.
    this.torchSpots = geometry.torches.map((t) => t.pos);
    for (let i = 0; i < this.torches.length; i++) {
      this.torchBase[i] = geometry.torchIntensity;
      this.torchHeld[i] = -1;
      this.torchTarget[i] = 0;
      this.torchFade[i] = 0;
      this.torches[i]!.intensity = 0;
      this.torches[i]!.distance = geometry.torchRange;
      this.torches[i]!.color.set(lit.torchColor);
    }

    // Skylights, up to the budget. Any the map does not use are parked dark
    // rather than removed, so the scene's light count never moves.
    const skylights = geometry.moonlights ?? [];
    for (let i = 0; i < this.moonlights.length; i++) {
      const light = this.moonlights[i]!;
      const at = skylights[i];
      light.color.set(lit.moonColor);
      light.intensity = at ? MOONLIGHT_INTENSITY : 0;
      if (at) light.position.set(at.pos.x, at.pos.y, at.pos.z);
    }
  }

  /**
   * Swaps the level under a running world, for a map change between rounds.
   *
   * Everything outside the level survives: the scene, the camera's programs, the
   * composer, the actors, the player's capsule and the shader warm-up done at
   * load. What goes is this map's meshes and its colliders; what arrives is the
   * next one's.
   *
   * The light *counts* deliberately do not move — see `buildLevel` and the torch
   * pool. A different number of lights is a different shader program for every
   * lit material in the arena, and recompiling those is the several-second stall
   * that made a mid-set map change unthinkable in the first place.
   */
  rebuildLevel(geometry: LevelGeometry): void {
    if (this.levelGroup) {
      this.scene.remove(this.levelGroup);
      /*
       * Geometry is disposed; materials are not.
       *
       * Box geometries are built per wall for this map and nothing else will
       * ever use them. Materials are shared — `materials()` hands out one per
       * kind for the whole session, and the asset registry's are shared across
       * every prop — so disposing them here would free something the next map is
       * about to ask for and force a recompile, which is exactly what this
       * method exists to avoid.
       */
      this.levelGroup.traverse((n) => {
        const mesh = n as THREE.Mesh;
        if (mesh.isMesh && mesh.geometry) mesh.geometry.dispose();
      });
      this.levelGroup = null;
    }

    /*
     * Level colliders, sky lids and spell barriers all go here, together:
     * `buildLevel` below asks the shared builder for the new map, and the builder
     * clears whatever it built for the old one first — barriers included, since
     * a Wall of Ice standing in mid-air on the next map is very funny once and
     * then ruins a round.
     */

    const lit = { ...DEFAULT_LIGHTING, ...(geometry.lighting ?? {}) };
    this.scene.background = new THREE.Color(lit.skyColor);
    (this.scene.fog as THREE.FogExp2).color.set(lit.fogColor);
    (this.scene.fog as THREE.FogExp2).density = lit.fogDensity;
    this.ambient.color.set(lit.moonColor);
    this.ambient.intensity = geometry.ambient;
    this.moon.color.set(lit.moonColor);
    this.moon.intensity = lit.moonIntensity;

    this.buildLevel(geometry);
  }

  /**
   * The shadow camera's own axes, worked out once.
   *
   * Three.js builds the light's view matrix with `lookAt` and the light's `up`,
   * so reproducing that basis here is what lets the centre be snapped along the
   * axes the depth map is actually sampled on. Snapping along world X and Z
   * instead would be snapping along the wrong lines and would not stop the
   * shimmer, only change its rhythm.
   */
  private buildShadowBasis(): void {
    const forward = this.shadowDir.clone().negate();
    this.shadowRight.crossVectors(this.moon.up, forward).normalize();
    this.shadowUp.crossVectors(forward, this.shadowRight).normalize();
  }

  /**
   * Resizes the shadow frustum, keeping it square.
   *
   * Exists because the right number is a matter of taste: too tight and static
   * shadows wink out at the boundary as you walk, too wide and the pass is
   * redrawing the whole arena for texels nobody can resolve. Both ends are
   * obvious in play and neither is visible in a profile, so this is here to be
   * turned while somebody watches.
   */
  setShadowExtent(metres: number): void {
    const half = Math.max(4, metres);
    const cam = this.moon.shadow.camera;
    cam.left = -half;
    cam.right = half;
    cam.top = half;
    cam.bottom = -half;
    cam.updateProjectionMatrix();
  }

  /**
   * Walks the shadow camera along with the player.
   *
   * A tight frustum that follows is the whole saving: the depth pass now draws
   * the geometry within `SHADOW_EXTENT` instead of every box in the Cathedral,
   * and the map's texels are spread over a fraction of the ground so the result
   * is sharper as well as cheaper.
   *
   * **Snapped to texels**, which is not optional. An orthographic shadow map
   * moving by arbitrary sub-texel amounts re-quantises every edge in it on every
   * frame, and the shadows crawl and fizz along their boundaries as you walk —
   * far more noticeable than the shadows themselves, and the reason this
   * technique gets abandoned by people who skip this part. Rounding the centre
   * to whole texels along the camera's own axes means a step of exactly one
   * texel or none at all, and the edges sit still.
   */
  followShadow(eye: Vec3): void {
    if (!this.moon.castShadow) return;

    const cam = this.moon.shadow.camera;
    const texel = (cam.right - cam.left) / this.moon.shadow.mapSize.x;

    this.shadowCentre.set(eye.x, eye.y, eye.z);
    /*
     * Two components, not three.
     *
     * Only `right` and `up` decide what an orthographic shadow camera sees:
     * sliding it along its own view direction changes which slice of the depth
     * range things land in and nothing about where they land in the map. So the
     * third component is *dropped* rather than snapped, which puts the centre on
     * a fixed plane and makes the snap exact.
     *
     * Keeping it was the first version and it did not work. The two snapped
     * components held still and the unsnapped third slid smoothly underneath
     * them, so the camera moved a couple of millimetres every frame — texels
     * where it counted, but never actually at rest, which is most of the way to
     * having no snapping at all.
     */
    const alongRight = Math.round(this.shadowCentre.dot(this.shadowRight) / texel) * texel;
    const alongUp = Math.round(this.shadowCentre.dot(this.shadowUp) / texel) * texel;

    this.shadowCentre
      .copy(this.shadowRight).multiplyScalar(alongRight)
      .addScaledVector(this.shadowUp, alongUp);

    this.moon.target.position.copy(this.shadowCentre);
    this.moon.position.copy(this.shadowCentre).addScaledVector(this.shadowDir, SHADOW_DISTANCE);
  }

  /**
   * Guttering torches.
   *
   * The cheapest thing in the entire lighting budget and the one that most stops
   * a dark room reading as a photograph of a dark room. Two sine waves at
   * unrelated rates rather than one, because a single sine reads as a pulse and
   * a flame does not pulse.
   */
  tickLights(elapsed: number, eye?: Vec3, dt = 1 / 60): void {
    if (eye) this.assignTorches(eye);

    const { torchFlicker, torchFlickerSpeed } = this.lighting;
    const step = Math.min(1, dt * TORCH_FADE_SPEED);

    for (let i = 0; i < this.torches.length; i++) {
      const light = this.torches[i]!;
      const fade = this.torchFade[i]! + (this.torchTarget[i]! - this.torchFade[i]!) * step;
      this.torchFade[i] = fade;

      // A slot with no fixture in it goes dark, never hidden: hiding it would
      // change the scene's light count and recompile every lit material.
      if (fade <= 0.001) {
        light.intensity = 0;
        continue;
      }
      const phase = this.torchPhase[i]!;
      const wobble = torchFlicker <= 0 ? 0
        : Math.sin(elapsed * torchFlickerSpeed + phase) * 0.6
          + Math.sin(elapsed * torchFlickerSpeed * 2.7 + phase * 1.9) * 0.4;
      light.intensity = this.torchBase[i]! * fade * (1 + wobble * torchFlicker);
    }
  }

  /**
   * How much of a fixture the eye can reach, from 1 (clear) down to
   * `TORCH_OCCLUDED_WEIGHT` (nothing).
   *
   * Several rays rather than one. A single ray to the flame makes a torch
   * flicker in and out as you strafe past a pillar, because the centre is
   * exactly the point most likely to be clipped by an edge — so the bracket is
   * sampled at a few offsets and partial visibility is a real answer.
   *
   * The result *scales* the distance rather than excluding the fixture, which
   * matters more than it sounds. A torch around a corner still lights the corner
   * you can see, and hard-excluding it takes away the spill that makes a
   * corridor read as lit rather than as a dark tube with a bright end. Penalised,
   * it wins a slot only when nothing better is competing for one.
   */
  private visibility(eye: Vec3, at: Vec3): number {
    let seen = 0;
    for (const [dx, dy, dz] of TORCH_SAMPLES) {
      const target = { x: at.x + dx, y: at.y + dy, z: at.z + dz };
      if (!this.segmentBlocked(eye, target)) seen++;
    }
    return visibilityWeight(seen, TORCH_SAMPLES.length);
  }

  /**
   * Hands the pool to whichever fixtures are nearest.
   *
   * Recomputed on a slow cadence rather than every frame. Walking changes which
   * torches are nearest at about the rate you walk, and a hundred distance
   * comparisons every frame to notice that is work for nothing.
   */
  private assignTorches(eye: Vec3): void {
    this.torchRecheck -= 1;
    if (this.torchRecheck > 0) return;
    this.torchRecheck = TORCH_RECHECK_FRAMES;

    /*
     * Nearest by distance, then scored by how much of each fixture can be seen.
     *
     * Distance alone hands slots to torches behind walls, which is not merely
     * wasteful — these lights cast no shadow, so a torch eight metres away
     * through a wall lights the wall's far side as though the wall were not
     * there. Selection is the only occlusion this game has.
     *
     * Only the nearest handful are tested. A ray costs little but a hundred of
     * them for fixtures that could never win a slot costs it for nothing.
     */
    const candidates = this.torchSpots
      .map((pos, i) => ({
        i,
        d: (pos.x - eye.x) ** 2 + (pos.y - eye.y) ** 2 + (pos.z - eye.z) ** 2,
      }))
      .sort((a, b) => a.d - b.d)
      .slice(0, this.torches.length * TORCH_CANDIDATE_FACTOR);

    /*
     * Cast from the eye, not the feet.
     *
     * `pos` is the floor a mage is standing on, and a ray from there to a torch
     * three metres up a wall grazes every doorway lintel and step between here
     * and there — half the room would come back occluded while plainly in view.
     */
    const from = { x: eye.x, y: eye.y + STAND_EYE, z: eye.z };
    const ranked = candidates
      .map((c) => ({ i: c.i, score: c.d / this.visibility(from, this.torchSpots[c.i]!) }))
      .sort((a, b) => a.score - b.score)
      .slice(0, this.torches.length)
      .map((r) => r.i);

    /*
     * Keeping a slot on the fixture it already had, wherever possible.
     *
     * Reassigning purely by rank re-sorts the whole pool every time the ranking
     * shifts by one, so a single step could move all fourteen lights at once.
     * At a 34m range that was invisible — the room was lit either way. At 8m it
     * is fourteen torches blinking, so which slot holds which fixture has to be
     * as stable as the set allows.
     */
    const wanted = new Set(ranked);
    const kept = new Set<number>();
    this.torchHeld.forEach((held, slot) => {
      if (held >= 0 && wanted.has(held)) kept.add(slot);
    });
    const spare = ranked.filter(
      (i) => !this.torchHeld.some((held, slot) => held === i && kept.has(slot)),
    );

    let next = 0;
    this.torches.forEach((light, slot) => {
      if (kept.has(slot)) return;
      const take = spare[next++];
      this.torchHeld[slot] = take ?? -1;
      if (take === undefined) return;
      const spot = this.torchSpots[take]!;
      light.position.set(spot.x, spot.y, spot.z);
    });

    /*
     * Faded in and out rather than switched. A slot that changes hands still
     * jumps, and a light appearing at full brightness a few metres away is the
     * most obvious thing on screen — so `torchFade` carries it up from nothing
     * and `tickLights` scales by it. Intensity, never visibility: hiding a light
     * changes the scene's light count and recompiles every lit material.
     */
    this.torchHeld.forEach((held, slot) => {
      this.torchTarget[slot] = held >= 0 ? 1 : 0;
    });
  }

  step(): void {
    this.physics.step();
  }
}

export async function initPhysics(): Promise<void> {
  await RAPIER.init();
}
